// ✅ src/pages/Orders.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AllOrdersThunk,
  DeleteOrdersThunk,
} from "../../redux/states/orders/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { Eye, Delete, Search } from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Orders = () => {
  const dispatch = useDispatch();
  const { orders, loading, error } = useSelector((state) => state.orders);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(AllOrdersThunk());
  }, [dispatch]);

  useEffect(() => {
    if (orders) {
      const filtered = orders.filter(
        (order) =>
          order.order_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.user_id_data
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.payment_status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrders(filtered);
    }
  }, [orders, searchTerm]);

  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteOrdersThunk(selectedId));
      setModalOpen(false);
      setSelectedId(null);
      toast.success("Order deleted successfully!");
    }
  };

  const handleView = (uuid) => {
    navigate(`/view-orders/${uuid}`);
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-2">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">All Orders</h1>

        {/* Search */}
        <div className="mb-4 flex items-center gap-2">
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  S.No
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  Order Number
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  User
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  Payment
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  Total
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  Payment Method
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">
                  Created At
                </th>
                <th className="px-4 py-2 text-center text-sm font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredOrders.length > 0 ? (
                filteredOrders.map((order, index) => (
                  <tr key={order.uuid} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{index + 1}</td>
                    <td className="px-4 py-3 text-sm flex items-center gap-2">
                      <span>{order.order_number}</span>

                      {order.view_status === "unseen" && (
                        <span className="bg-red-600 text-white text-[10px] px-2 py-[2px] rounded-full animate-pulse">
                          NEW
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-sm">{order.user_id_data}</td>
                    <td className="px-4 py-3 text-sm">
                      {order.payment_status}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          order.order_status === "confirmed"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {order.order_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{order.total} Rs</td>
                    <td className="px-4 py-3 text-sm">
                      {order.payment_method}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {order.created_at.slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-center flex justify-center gap-2">
                      <button
                        onClick={() => handleView(order.uuid)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View"
                      >
                        <Eye />
                      </button>
                      {/* <button
                        onClick={() => handleEdit(order.uuid)}
                        className="text-yellow-600 hover:text-yellow-800"
                        title="Edit"
                      >
                        <Edit />
                      </button> */}
                      <button
                        onClick={() => handleDeleteClick(order.uuid)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Delete />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Delete Modal */}
        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Order"
          message="Do you really want to delete this order? This action cannot be undone."
        />
      </div>
    </NonAuthLayout>
  );
};

export default Orders;
